import React, { useEffect, useState } from 'react';
import { CheckCircle, Sparkles, ArrowRight, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/payment';

interface PaymentSuccessProps {
  onBack: () => void;
}

const PaymentSuccess: React.FC<PaymentSuccessProps> = ({ onBack }) => {
  const { user, profile } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    // Verify payment status
    const verifyPayment = async () => {
      try {
        // Get session ID from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id') || 'demo_session';
        
        // Verify with payment service
        const isValid = await paymentService.verifyPaymentStatus(sessionId);
        
        if (isValid && user) {
          // Update subscription status
          await paymentService.handlePaymentSuccess(user, 'pro_monthly');
        }
        
        setVerified(isValid);
      } catch (error) {
        console.error('Error verifying payment:', error);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [user]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-8">
        {verifying ? (
          <div className="text-center">
            <Loader className="w-16 h-16 mx-auto mb-4 text-cyan-500 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Verifying Payment...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your subscription
            </p>
          </div>
        ) : (
          <>
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-green-500 to-emerald-500 rounded-full p-5">
                  <CheckCircle className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Welcome to Pro! 🎉
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Your subscription has been activated successfully
              </p>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                You now have access to:
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Unlimited prompt saving
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  No API keys required
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Premium AI models
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Priority support
                </li>
              </ul>
            </div>

            {/* Next Steps */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong className="text-gray-900 dark:text-white">Next steps:</strong>
              </p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <li>Start creating and saving prompts</li>
                <li>Explore AI-powered features</li>
                <li>Share your creations with the community</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={onBack}
                className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-sky-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-sky-600 transition-all flex items-center justify-center gap-2"
              >
                Start Creating
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full py-3 px-6 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                Go to Dashboard
              </button>
            </div>

            {/* Support */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
              Need help? Contact{' '}
              <a href="mailto:support@fxprompt.app" className="text-cyan-600 hover:underline">
                support@fxprompt.app
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;